import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';
import ConfirmModal from '../../components/common/ConfirmModal';
import AppSpinner from '../../components/AppSpinner';

interface UserData {
  _id?: string;
  userId: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: string; 
  status: string;
  age?: number | '';
  gender?: string;
  phoneNumber?: string;
  referralCode?: string;
  lastLoginIp?: string;
  password?: string; 
}

const AdminUserFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isEditMode = Boolean(id);

  const queryParams = new URLSearchParams(location.search);
  const initialRole = queryParams.get('role') || 'user';

  const [formData, setFormData] = useState<UserData>({
    userId: '',
    email: '',
    displayName: '',
    role: initialRole,
    status: '정상',
    createdAt: new Date().toISOString().split('T')[0], 
    age: '',
    gender: 'male',
    phoneNumber: '',
    referralCode: '',
    lastLoginIp: '',
    password: '', 
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  // 현재 컨텍스트가 상담사 관련인지 판단
  const isCounselorContext = isEditMode ? formData.role === 'counselor' : initialRole === 'counselor';
  const roleLabel = isCounselorContext ? '상담사' : '사용자';

  useEffect(() => {
    if (!isAuthenticated) return;

    if (isEditMode && id) {
      const fetchUser = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`/api/admin/users/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          const userData = response.data;
          const formattedDate = userData.createdAt 
            ? new Date(userData.createdAt).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0];

          setFormData({
            ...userData,
            role: userData.role || 'user',
            status: userData.status || '정상',
            createdAt: formattedDate,
            age: userData.age || '',
            gender: userData.gender || 'male',
            phoneNumber: userData.phoneNumber || '',
            referralCode: userData.referralCode || '',
            lastLoginIp: userData.lastLoginIp || '',
            password: '', 
          });
        } catch (err) {
          console.error('Failed to fetch user:', err);
          setError('사용자 정보를 불러오는 데 실패했습니다.');
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    }
  }, [isAuthenticated, id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveClick = () => {
    setIsSaveModalOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEditMode ? `/api/admin/users/${id}` : `/api/admin/users`;
      const method = isEditMode ? 'put' : 'post';
      
      const payload = {
        ...formData,
        fullName: formData.displayName, 
      };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (formData.role === 'counselor') {
        navigate('/admin/counselors');
      } else {
        navigate('/admin/users');
      }
    } catch (err) {
      console.error('Failed to save user:', err);
      setError('사용자 저장에 실패했습니다.');
      alert('저장에 실패했습니다.');
    } finally {
      setLoading(false);
      setIsSaveModalOpen(false);
    }
  };

  const handleGoBack = () => {
    if (formData.role === 'counselor') {
      navigate('/admin/counselors');
    } else {
      navigate('/admin/users');
    }
  };

  if (loading && isEditMode && !formData.userId) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>{isEditMode ? `${roleLabel} 수정` : `${roleLabel} 등록`}</h1>

      <div className="detail-table-container">
        <table className="detail-table">
          <colgroup>
            <col style={{ width: '120px' }} />
            <col />
            <col style={{ width: '120px' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th>이름</th>
              <td colSpan={3}>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="이름을 입력하세요"
                />
              </td>
            </tr>
            <tr>
              <th>아이디</th>
              <td colSpan={3}>
                <input
                  type="text"
                  name="userId"
                  value={formData.userId}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="아이디를 입력하세요"
                  readOnly={isEditMode}
                  style={isEditMode ? { backgroundColor: '#f5f5f5' } : {}}
                />
              </td>
            </tr>
            <tr>
              <th>권한</th>
              <td colSpan={3}>
                <select 
                  name="role" 
                  value={formData.role} 
                  onChange={handleChange}
                  className="form-input"
                >
                  {isCounselorContext ? (
                    <option value="counselor">상담사</option>
                  ) : (
                    <>
                      <option value="user">사용자</option>
                      <option value="admin">관리자</option>
                    </>
                  )}
                </select>
              </td>
            </tr>
            <tr>
              <th>이메일</th>
              <td colSpan={3}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="이메일을 입력하세요"
                />
              </td>
            </tr>
            <tr>
              <th>비밀번호</th>
              <td colSpan={3}>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder={isEditMode ? "비밀번호 변경 시에만 입력하세요" : "비밀번호를 입력하세요"}
                />
              </td>
            </tr>
            <tr>
              <th>상태</th>
              <td colSpan={3}>
                <div className="radio-group">
                  <label className="custom-radio">
                    정상
                    <input
                      type="radio"
                      name="status"
                      value="정상"
                      checked={formData.status === '정상'}
                      onChange={handleRadioChange}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <label className="custom-radio">
                    정지
                    <input
                      type="radio"
                      name="status"
                      value="정지"
                      checked={formData.status === '정지'}
                      onChange={handleRadioChange}
                    />
                    <span className="checkmark"></span>
                  </label>
                </div>
              </td>
            </tr>
            <tr>
              <th>가입일</th>
              <td colSpan={3}>
                <input
                  type="date"
                  name="createdAt"
                  value={formData.createdAt}
                  onChange={handleChange}
                  className="form-input"
                />
              </td>
            </tr>
            <tr>
              <th>나이</th>
              <td>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="나이를 입력하세요"
                />
              </td>
              <th>성별</th>
              <td>
                <div className="radio-group">
                  <label className="custom-radio">
                    남자
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleRadioChange}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <label className="custom-radio">
                    여자
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleRadioChange}
                    />
                    <span className="checkmark"></span>
                  </label>
                </div>
              </td>
            </tr>
            <tr>
              <th>휴대폰 번호</th>
              <td>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="연락처를 입력하세요"
                />
              </td>
              <th>추천인 코드</th>
              <td>
                <input
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="코드"
                />
              </td>
            </tr>
            {isEditMode && (
              <tr>
                <th>접근 IP</th>
                <td colSpan={3}>
                  <input
                    type="text"
                    name="lastLoginIp"
                    value={formData.lastLoginIp}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="최근 접속 IP"
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bottom-actions">
        <button className="btn-list" onClick={handleGoBack}>
          ≡ 목록
        </button>
        <button className="btn-register" onClick={handleSaveClick}>
          {isEditMode ? '수정' : '등록'}
        </button>
      </div>

      <ConfirmModal
        isOpen={isSaveModalOpen}
        title={isEditMode ? '수정하시겠습니까?' : '등록하시겠습니까?'}
        message={isEditMode ? `해당 정보로 ${roleLabel} 정보를 수정하시겠습니까?` : `해당 정보로 ${roleLabel}를 등록하시겠습니까?`}
        confirmText={isEditMode ? '수정' : '등록'}
        onConfirm={handleSubmit}
        onCancel={() => setIsSaveModalOpen(false)}
      />
    </div>
  );
};

export default AdminUserFormPage;